import { Link } from 'react-router-dom';
import {
  AMAZON_REMOVAL_FEE_ROWS,
  INVENTORY_DISPOSAL_OPTIONS,
  PPP_REMOVAL_HANDLING_ROWS,
  REMOVAL_ORDER_FAQS,
} from '../../data/removalOrdersData';
import FAQSection from '../FAQSection';
import './RemovalOrdersSection.css';

const removalOrderFaqs = REMOVAL_ORDER_FAQS.map((faq, index) => {
  if (index !== REMOVAL_ORDER_FAQS.length - 1) {
    return { question: faq.question, answer: faq.answer };
  }
  return {
    question: faq.question,
    answer: (
      <>
        {faq.answer}{' '}
        <Link to="/blog/how-to-handle-amazon-removal-orders-uk">
          Read the complete UK removal orders guide
        </Link>
        .
      </>
    ),
  };
});

export default function RemovalOrdersSection() {

  return (
    <section
      className="rp-removal"
      aria-labelledby="rp-removal-heading"
      id="amazon-removal-orders"
    >
      <div className="rp2-container">
        <div className="rp-removal-head">
          <span className="rp2-tag">Amazon FBA removals</span>
          <h2 id="rp-removal-heading" className="rp2-section-title">
          Amazon FBA Removal Orders Managed by Pick Pack Pro
          </h2>
          <p className="rp-removal-lead">
            Amazon FBA removal orders differ from customer returns because they are seller-initiated inventory
            removals from Amazon fulfilment centres. Pick Pack Pro receives, inspects, grades, and processes
            every removal order at our{' '}
            <Link to="/fulfilment-centre-milton-keynes">Milton Keynes warehouse</Link> before restocking
            through <Link to="/seller-fulfilled-prime-services">Seller Fulfilled Prime (SFP)</Link>, re-prepping
            via <Link to="/amazon-fba-prep-services">Amazon FBA prep</Link>, or disposing of inventory according
            to your instructions. For fee strategy, see how sellers{' '}
            <Link to="/blog/reduce-amazon-fba-storage-fees-drip-feed">reduce Amazon FBA storage fees</Link>.
          </p>
        </div>

        <div className="rp-removal-grid">
          <div className="rp-removal-panel">
            <h3 className="rp-removal-panel-title">Amazon Removal Order Fees UK: Estimate Your Costs</h3>
            <p className="rp-removal-panel-note">
            Use these Amazon removal order fee ranges as a planning guide before submitting inventory removals. Always confirm the latest charges in Seller Central, as Amazon fees vary by product size, weight, and marketplace policies.
            </p>
            <div className="rp-removal-table-wrap" tabIndex={0} role="region" aria-label="Amazon removal fee table">
              <table className="rp-removal-table">
                <thead>
                  <tr>
                    <th scope="col">Size band</th>
                    <th scope="col">Estimated Amazon Removal Fee (GBP)</th>
                    <th scope="col">Planning Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {AMAZON_REMOVAL_FEE_ROWS.map((row) => (
                    <tr key={row.band}>
                      <th scope="row">{row.band}</th>
                      <td>{row.amazonFee}</td>
                      <td>{row.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rp-removal-panel rp-removal-panel--accent">
            <h3 className="rp-removal-panel-title">Pick Pack Pro handling (after arrival)</h3>
            <p className="rp-removal-panel-note">
              Transparent line items when removal stock lands at our UK prep centre.
            </p>
            <ul className="rp-removal-fee-list">
              {PPP_REMOVAL_HANDLING_ROWS.map((row) => (
                <li key={row.service}>
                  <span className="rp-removal-fee-service">{row.service}</span>
                  <span className="rp-removal-fee-price">{row.fee}</span>
                </li>
              ))}
            </ul>
            <Link to="/amazon-fba-prep-pricing" className="rp-removal-cta">
              View FBA prep pricing →
            </Link>
          </div>
        </div>

        <div className="rp-removal-disposal">
          <h3 className="rp-removal-disposal-title">
            Amazon Inventory Disposal Options 2026: Removal, Liquidation or Storage?
          </h3>
          <p className="rp-removal-disposal-lead">
            Before confirming an Amazon removal order, compare your inventory recovery options. Choosing
            removal, liquidation, or continued storage depends on product value, storage costs, demand, and your
            long-term strategy — including whether stock should return to{' '}
            <Link to="/amazon-fba-prep-pricing">FBA prep pricing</Link> workflows or stay in 3PL storage after
            processing. Full walkthrough:{' '}
            <Link to="/blog/how-to-handle-amazon-removal-orders-uk">how to handle Amazon removal orders UK</Link>.
          </p>
          <div className="rp-removal-table-wrap" tabIndex={0} role="region" aria-label="Inventory disposal options comparison">
            <table className="rp-removal-table rp-removal-table--comparison">
              <thead>
                <tr>
                  <th scope="col">Option</th>
                  <th scope="col">Best For</th>
                  <th scope="col">Key Considerations</th>
                </tr>
              </thead>
              <tbody>
                {INVENTORY_DISPOSAL_OPTIONS.map((row) => (
                  <tr key={row.option}>
                    <th scope="row">{row.option}</th>
                    <td>{row.bestFor}</td>
                    <td>{row.keyConsiderations}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      <FAQSection
        headingId="removal-order-faqs"
        className="faqs-section rp-removal-faqs-section"
        title="Amazon Removal Order FAQs"
        description="Find answers to common questions about Amazon FBA removal orders, inventory removal, disposal options, fees, and Seller Central processes."
        faqs={removalOrderFaqs}
        showImage={false}
        schemaScriptId="removal-order-faq-ui"
      />
    </section>
  );
}
